/**
 * @name FileList.js
 * @author 김권영
 * @since 2025. 01. 06.
 * @description
 * 공통 파일 목록 및 다운로드 삭제 기능
 * 
 * @param {string} atchFileId - 파일ID (파일PK번호)
 * @param {function} onFileDeleteMark - 파일 삭제 시 삭제 할 파일 순번 (폼 저장 방식때 사용 - 따로 구현 필요 (userBoardFormModal.js에 markDelete() 함수 ))
 * @param {boolean} showDelete - 삭제 버튼 활성화 여부 (기본 값 ture)
 * @param {className} className - 파일 <ul className=""> 지정
 * @param {array} deletedFileSns - 기존 파일 중 최종 삭제 예정 파일 순번 fileSn 배열
 */
import React  from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { get, del } from '@utils/axios_api';
import './FileList.css';

const fetchFileDetails = async (atchFileId, saccCode) => {
  const response = await get('/file/getFileDetails', {
    params: { atchFileId: atchFileId ?? '', saccCode: saccCode ?? '' },
  });
  return response.result;
};

const FileList = ({ atchFileId, saccCode, onFileDeleteMark, showDelete = true, className, deletedFileSns = [] }) => {
  const queryClient = useQueryClient();

  const { data: existingFiles = [], isLoading, isError, error} = useQuery(
    ['fileDetails', atchFileId ?? '', saccCode ?? ''],
    () => fetchFileDetails(atchFileId, saccCode),
    { enabled: !!(atchFileId || saccCode),
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
     } 
  );


  const handleDownload = async (file) => {
    try {
      const blob = await get('/file/download', {
        params: {
          atchFileId: file.atchFileId,
          fileSn: file.fileSn,
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.orignlFileNm);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('파일 다운로드 중 오류 발생:', error);
    }
  };

  const handleDeleteClick  = async (file) => {
    if (!window.confirm(`${file.orignlFileNm} 파일을 삭제하시겠습니까?`)) return;
    try {
      await del(`/file/${file.atchFileId}/${file.fileSn}`);

      await queryClient.invalidateQueries(['fileDetails', atchFileId ?? '', saccCode ?? '']);
    } catch (e) {
      console.error('파일 삭제 실패:', e);
    }
  };


  if (!atchFileId && !saccCode) return null;
  if (isLoading) return <div>파일 목록을 불러오는 중입니다...</div>;
  if (isError) return <div>파일 목록을 불러오는 중 오류가 발생했습니다: {error.message}</div>;
  if (existingFiles.length === 0) return null;

  const displayedFiles = existingFiles.filter(
    (file) => !deletedFileSns.includes(file.fileSn)
  );

  // 표시할 파일이 0개라면 null 반환
  if (displayedFiles.length === 0) return null;


  return (
    <ul className={`existing-files ${className}`}>
      {displayedFiles.map((file) => (
        <li key={file.fileSn}>
           <div className="file-item-container">
              <button type="button" onClick={() => handleDownload(file)} className="file-download-button" >{file.orignlFileNm}</button>
              {showDelete && (
                <button type="button" onClick={() => handleDeleteClick(file)} className="file-delete-button">X</button>
              )}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default FileList;
